import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import {
  VehicleTimeline,
  VehicleTimelineNote,
  VehicleTimelineNoteAttachment,
  VehicleTimelineNoteCategory,
} from '@otw/models/timeline'
import { vehicleTimelineConverter } from '@otw/models/converters'
import { getLastVehicleTimelineNotesUpdateMs } from '@/utils/repairLastUpdate'

const COLLECTION = 'vehicle_timeline'

/** Persists a single note on `vehicle_timeline` (same shape as {@link useVehicleTimeline} addNote). */
export async function appendVehicleTimelineNote(
  vehicleId: string,
  args: {
    text: string
    created_by_user_id?: string
    category?: VehicleTimelineNoteCategory
    attachments?: VehicleTimelineNoteAttachment[]
  }
): Promise<void> {
  const docRef = doc(db, COLLECTION, vehicleId).withConverter(vehicleTimelineConverter)
  const newNote: VehicleTimelineNote = {
    text: args.text,
    created_at: Timestamp.now(),
    ...(args.created_by_user_id && { created_by_user_id: args.created_by_user_id }),
    ...(args.category && args.category !== 'generic' && { category: args.category }),
    ...(args.attachments && args.attachments.length > 0 && { attachments: args.attachments }),
  }
  const snapshot = await getDoc(docRef)
  const now = Timestamp.now()
  if (!snapshot.exists()) {
    const newTimeline: VehicleTimeline = {
      id: vehicleId,
      vehicle_id: vehicleId,
      notes: [newNote],
      updated_at: now,
    }
    await setDoc(docRef, newTimeline)
    return
  }
  const current = snapshot.data()
  const notes = [...(current?.notes ?? []), newNote]
  await updateDoc(docRef, { notes, updated_at: now })
}

async function loadVehicleTimeline(vehicleId: string): Promise<VehicleTimeline | null> {
  const docRef = doc(db, COLLECTION, vehicleId).withConverter(vehicleTimelineConverter)
  const snapshot = await getDoc(docRef)
  if (!snapshot.exists()) return null
  return snapshot.data()
}

/** Loads `vehicle_timeline` for many vehicles (e.g. dashboard averages). */
export function useVehicleTimelinesByVehicleIds(
  vehicleIds: string[],
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled !== false
  const sortedUniqueIds = useMemo(() => {
    const unique = new Set(vehicleIds.filter((id): id is string => Boolean(id)))
    return [...unique].sort((a, b) => a.localeCompare(b))
  }, [vehicleIds.join('\0')])

  const queries = useMemo(
    () =>
      sortedUniqueIds.map((id) => ({
        queryKey: ['vehicle_timeline', id] as const,
        queryFn: () => loadVehicleTimeline(id),
        staleTime: 1000 * 60,
        enabled: enabled && sortedUniqueIds.length > 0,
      })),
    [sortedUniqueIds, enabled]
  )

  const results = useQueries({ queries })

  return useMemo(() => {
    const map = new Map<string, VehicleTimeline | null>()
    sortedUniqueIds.forEach((id, index) => {
      map.set(id, results[index]?.data ?? null)
    })
    return map
  }, [sortedUniqueIds, results])
}

/** Per-vehicle latest note time for repair grids; shares query keys with {@link useVehicleTimeline}. */
export function useVehicleTimelineLatestNoteMsMap(vehicleIds: (string | undefined | null)[]) {
  const ids = useMemo(
    () => [...new Set(vehicleIds.filter((id): id is string => Boolean(id)))],
    [vehicleIds],
  )

  const queries = useMemo(
    () =>
      ids.map((id) => ({
        queryKey: ['vehicle_timeline', id] as const,
        queryFn: () => loadVehicleTimeline(id),
        staleTime: 1000 * 60,
      })),
    [ids],
  )

  const results = useQueries({ queries })

  return useMemo(() => {
    const map = new Map<string, number>()
    ids.forEach((id, index) => {
      const timeline = results[index]?.data
      const ms = getLastVehicleTimelineNotesUpdateMs(timeline ?? null)
      if (ms != null) map.set(id, ms)
    })
    return map
  }, [ids, results])
}

export function useVehicleTimeline(vehicleId: string | undefined) {
  const queryClient = useQueryClient()

  const docRef = vehicleId
    ? doc(db, COLLECTION, vehicleId).withConverter(vehicleTimelineConverter)
    : null

  const queryKey = ['vehicle_timeline', vehicleId]

  const { data: vehicleTimeline, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<VehicleTimeline | null> => {
      if (!vehicleId) return null
      return loadVehicleTimeline(vehicleId)
    },
    enabled: Boolean(vehicleId),
    staleTime: 1000 * 60,
  })

  const addNote = useMutation({
    mutationFn: async ({
      text,
      created_by_user_id,
      category,
      attachments,
    }: {
      text: string
      created_by_user_id?: string
      category?: VehicleTimelineNoteCategory
      attachments?: VehicleTimelineNoteAttachment[]
    }) => {
      if (!vehicleId) throw new Error('Vehicle ID required')
      await appendVehicleTimelineNote(vehicleId, {
        text,
        ...(created_by_user_id && { created_by_user_id }),
        ...(category && { category }),
        ...(attachments && attachments.length > 0 && { attachments }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updateNote = useMutation({
    mutationFn: async ({
      noteIndex,
      text,
    }: {
      noteIndex: number
      text: string
    }) => {
      if (!docRef) throw new Error('Vehicle ID required')
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) throw new Error('Timeline not found')
      const current = snapshot.data()
      const notes = [...(current?.notes ?? [])]
      if (noteIndex < 0 || noteIndex >= notes.length) throw new Error('Note not found')
      notes[noteIndex] = { ...notes[noteIndex], text }
      const now = Timestamp.now()
      await updateDoc(docRef, { notes, updated_at: now })
      return { notes, updated_at: now }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const deleteNote = useMutation({
    mutationFn: async ({ noteIndex }: { noteIndex: number }) => {
      if (!docRef) throw new Error('Vehicle ID required')
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) throw new Error('Timeline not found')
      const current = snapshot.data()
      const notes = (current?.notes ?? []).filter((_: unknown, i: number) => i !== noteIndex)
      const now = Timestamp.now()
      await updateDoc(docRef, { notes, updated_at: now })
      return { notes, updated_at: now }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    vehicleTimeline: vehicleTimeline ?? undefined,
    isLoading,
    addNote,
    updateNote,
    deleteNote,
  }
}
